import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import { Box, Typography, IconButton, Chip } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import logo from "../../assets/images/logo.png";

import MenuIcon from "@mui/icons-material/Menu";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import useLocalization from "../../hooks/useLocalization";
import useData from "../../hooks/useData";
import DocumentationIcon from "../documentation/DocumentationIcon";

import PageMenu from "./PageMenu";

const PageHeader = ({ pageName, entityName, backPath, backState }) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const { getLabel } = useLocalization();
  const { executeAction } = useData();
  const role = useSelector((state) => state.authentication.role);

  const [openMenu, setOpenMenu] = useState(false);
  const [hasPendingExpenseApprovals, setHasPendingExpenseApprovals] =
    useState(false);
  const [hasDurationOutliers, setHasDurationOutliers] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const loadPendingExpenseIndicator = async () => {
      if (role === "rep") {
        if (isMounted) {
          setHasPendingExpenseApprovals(false);
          setHasDurationOutliers(false);
        }
        return;
      }

      const [expenseResults, outlierResults] = await Promise.all([
        executeAction({
          entityName: "Expense",
          action: "getPendingApprovalsIndicator",
          data: {},
        }),
        executeAction({
          entityName: "Call",
          action: "getDurationOutliersIndicator",
          data: {},
        }),
      ]);

      const hasPendingExpenseApprovals =
        expenseResults?.isSuccessful &&
        !!expenseResults?.data?.hasPendingExpenseApprovals;
      const hasPendingDurationOutliers =
        outlierResults?.isSuccessful &&
        !!outlierResults?.data?.hasDurationOutliers;

      if (isMounted) {
        setHasPendingExpenseApprovals(!!hasPendingExpenseApprovals);
        setHasDurationOutliers(!!hasPendingDurationOutliers);
      }
    };

    loadPendingExpenseIndicator();

    return () => {
      isMounted = false;
    };
  }, [role, pageName]);

  const handleBack = () => {
    if (backPath) {
      navigate(backPath, { state: backState || {} });
    }
  };

  const handleHome = () => {
    navigate("/home");
  };

  return (
    <>
      <Box
        height="50px"
        backgroundColor={theme.palette.primary.light}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        paddingLeft="10px"
        paddingRight="10px"
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            minWidth: "99px",
          }}
        >
          {backPath && (
            <IconButton
              onClick={handleBack}
              size="small"
              sx={{ color: "black" }}
            >
              <ArrowBackIcon />
            </IconButton>
          )}
          <Box
            sx={{ cursor: "pointer" }}
            onClick={handleHome}
          >
            <img
              src={logo}
              height="40px"
            />
          </Box>
        </Box>

        {entityName ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            height="50px"
          >
            <Typography variant="overline">{getLabel(pageName)}</Typography>
            <Typography
              variant="h6"
              sx={{ marginTop: "-10px" }}
            >
              {getLabel(entityName)}
            </Typography>
          </Box>
        ) : (
          <Typography variant="h5">{getLabel(pageName)}</Typography>
        )}

        <Box
          sx={{
            minWidth: "99px",
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
            alignItems: "center",
          }}
        >
          <DocumentationIcon pageName={pageName} />
          {hasPendingExpenseApprovals ? (
            <Chip
              label={getLabel("pendingExpenseApprovals")}
              size="small"
              color="warning"
              onClick={() => navigate("/expenses")}
              clickable
              sx={{ fontWeight: 600 }}
            />
          ) : null}
          {role !== "rep" && role !== "client" ? (
            <Chip
              label={
                hasDurationOutliers
                  ? getLabel("pendingCallDurationOutliers")
                  : getLabel("callDurationOutliers")
              }
              size="small"
              color={hasDurationOutliers ? "warning" : "default"}
              onClick={() => navigate("/call-duration-outliers")}
              clickable
              sx={{ fontWeight: 600 }}
              variant={hasDurationOutliers ? "filled" : "outlined"}
            />
          ) : null}
          <MenuIcon onClick={() => setOpenMenu(true)} />
        </Box>
      </Box>

      <PageMenu
        open={openMenu}
        onClose={() => setOpenMenu(false)}
      />
    </>
  );
};

export default PageHeader;
